import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useTheme,
} from '@mui/material'
import Delete from 'assets/icons/Delete.png'
import {
  Background,
  Container,
  LeftContainer,
  SettingsButton,
} from './settings-details.styles'
import {
  useAddDispatchUserMutation,
  useDeleteDispatchUserMutation,
  useGetDispatchUsersQuery,
  useUpdateDispatchUserMutation,
} from 'store/company/dispatch-users.api'
import { useLazyGetUserByEmailQuery } from 'store/auth/auth.api'
import { useContext, useState } from 'react'
import SubmitLockButton from 'components/submit-lock-button/submit-lock-button.component'
import {
  SettingsContext,
  SettingsProviderValue,
} from '../settings.provider'

const SettingsUserManagement = () => {
  const theme = useTheme()
  const { user } = useContext(
    SettingsContext
  ) as unknown as SettingsProviderValue

  const [newUserEmail, setNewUserEmail] = useState('')
  const [newUserRole, setNewUserRole] = useState<'DISPATCH' | 'SUPER_ADMIN'>(
    'DISPATCH'
  )
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false)
  const [editUserRole, setEditUserRole] = useState<'DISPATCH' | 'SUPER_ADMIN'>(
    'DISPATCH'
  )
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null)
  const [addUserError, setAddUserError] = useState<string | null>(null)

  const {
    data: dispatchUsers,
    isLoading,
    isFetching,
  } = useGetDispatchUsersQuery()

  const [addDispatchUser, { isLoading: isAdding }] =
    useAddDispatchUserMutation()
  const [updateDispatchUser, { isLoading: isUpdating }] =
    useUpdateDispatchUserMutation()
  const [deleteDispatchUser, { isLoading: isDeleting }] =
    useDeleteDispatchUserMutation()
  const [getUserByEmail] = useLazyGetUserByEmailQuery()

  const handleAddDispatchUser = async () => {
    const trimmedEmail = newUserEmail.trim()
    if (!trimmedEmail) return

    const emailInDispatch = dispatchUsers?.some(
      (u) => u.email.toLowerCase() === trimmedEmail.toLowerCase()
    )
    const emailIsMainUser =
      user?.email?.toLowerCase() === trimmedEmail.toLowerCase()

    if (emailInDispatch || emailIsMainUser) {
      setAddUserError('A user with this email already exists.')
      return
    }

    try {
      const mainUserResult = await getUserByEmail({ email: trimmedEmail })
      if (mainUserResult.data) {
        setAddUserError('A user with this email already exists.')
        return
      }
    } catch {
      // Email not in main user table (e.g. 404) - continue
    }

    try {
      await addDispatchUser({
        email: trimmedEmail,
        role: newUserRole,
      }).unwrap()
      setNewUserEmail('')
      setNewUserRole('DISPATCH')
      setIsDialogOpen(false)
      setAddUserError(null)
    } catch (e) {
      console.error('Failed to add dispatch user', e)
    }
  }

  const handleOpenEditDialog = (id: string, role: 'DISPATCH' | 'SUPER_ADMIN') => {
    setSelectedUserId(id)
    setEditUserRole(role)
    setIsEditDialogOpen(true)
  }

  const handleUpdateDispatchUser = async () => {
    if (!selectedUserId) return

    try {
      await updateDispatchUser({
        id: selectedUserId,
        role: editUserRole,
      }).unwrap()
      setIsEditDialogOpen(false)
      setSelectedUserId(null)
    } catch (e) {
      console.error('Failed to update dispatch user', e)
    }
  }

  const handleDeleteDispatchUser = async (id: string) => {
    try {
      await deleteDispatchUser(id).unwrap()
    } catch (e) {
      console.error('Failed to delete dispatch user', e)
    }
  }

  return (
    <Box display="flex" flexDirection="column" height="100%">
      <Background theme={theme} background="dark">
        <Container>
          <LeftContainer />

          <Stack direction="row" spacing={2} alignItems="center">
            <SettingsButton
              variant="contained"
              size="small"
              onClick={() => {
                setNewUserEmail('')
                setNewUserRole('DISPATCH')
                setAddUserError(null)
                setIsDialogOpen(true)
              }}
            >
              Add New User
            </SettingsButton>
            <TextField
              size="small"
              placeholder="Search"
              sx={{
                minWidth: 200,
                backgroundColor: theme.palette.common.white,
                '& .MuiOutlinedInput-notchedOutline': {
                  border: 'none',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  border: 'none',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  border: 'none',
                },
                borderRadius: '5px',
              }}
            />
          </Stack>
        </Container>
      </Background>

      <Dialog
        open={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Add New User</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Stack spacing={2} mt={1}>
            <TextField
              label="Email"
              type="email"
              fullWidth
              value={newUserEmail}
              onChange={(e) => {
                setNewUserEmail(e.target.value)
                if (addUserError) {
                  setAddUserError(null)
                }
              }}
              error={!!addUserError}
              helperText={addUserError ?? ' '}
            />
            <FormControl fullWidth>
              <InputLabel id="dispatch-role-label">Role</InputLabel>
              <Select
                labelId="dispatch-role-label"
                label="Role"
                value={newUserRole}
                onChange={(e) =>
                  setNewUserRole(e.target.value as 'DISPATCH' | 'SUPER_ADMIN')
                }
              >
                <MenuItem value="SUPER_ADMIN">Super Admin</MenuItem>
                <MenuItem value="DISPATCH">Dispatch</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setIsDialogOpen(false)}>Cancel</Button>
          <SubmitLockButton
            variant="contained"
            onClick={handleAddDispatchUser}
            disabled={isAdding || !newUserEmail}
          >
            {isAdding ? 'Adding...' : 'Add User'}
          </SubmitLockButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={isEditDialogOpen}
        onClose={() => setIsEditDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Edit User Role</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Stack spacing={2} mt={1}>
            <FormControl fullWidth>
              <InputLabel id="edit-dispatch-role-label">Role</InputLabel>
              <Select
                labelId="edit-dispatch-role-label"
                label="Role"
                value={editUserRole}
                onChange={(e) =>
                  setEditUserRole(e.target.value as 'DISPATCH' | 'SUPER_ADMIN')
                }
              >
                <MenuItem value="SUPER_ADMIN">Super Admin</MenuItem>
                <MenuItem value="DISPATCH">Dispatch</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setIsEditDialogOpen(false)}>Cancel</Button>
          <SubmitLockButton
            variant="contained"
            onClick={handleUpdateDispatchUser}
            disabled={isUpdating}
          >
            {isUpdating ? 'Saving...' : 'Save'}
          </SubmitLockButton>
        </DialogActions>
      </Dialog>

      <Box p={3}>
        {(isLoading || isFetching) && (
          <Box display="flex" justifyContent="center" py={2}>
            <CircularProgress size={24} />
          </Box>
        )}

        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: '55%' }}>
                <Typography variant="subtitle2">Name</Typography>
              </TableCell>
              <TableCell sx={{ width: '30%' }}>
                <Typography variant="subtitle2">Role</Typography>
              </TableCell>
              <TableCell sx={{ width: '15%' }} align="right">
                <Typography variant="subtitle2"></Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {user && (
              <TableRow
                hover
                sx={{
                  backgroundColor: theme.palette.action.hover,
                }}
              >
                <TableCell>
                  <Stack spacing={0.5}>
                    <Typography
                      variant="body1"
                      color="text.primary"
                      sx={{ cursor: 'pointer', fontWeight: 700 }}
                    >
                      Owner
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {user.email}
                    </Typography>
                  </Stack>
                </TableCell>
                <TableCell>
                  <Stack spacing={0.5}>
                    <Typography variant="body1" sx={{ fontWeight: 700 }}>
                      Super Admin
                    </Typography>
                    <Typography variant="body2" color="text.primary">
                      Active
                    </Typography>
                  </Stack>
                </TableCell>
                <TableCell align="right" />
              </TableRow>
            )}

            {dispatchUsers?.map((dispatchUser, index) => (
              <TableRow
                key={dispatchUser.id}
                hover
                sx={{
                  backgroundColor:
                    index % 2 === 0
                      ? theme.palette.common.white
                      : theme.palette.action.hover,
                }}
              >
                <TableCell>
                  <Stack spacing={0.5}>
                    <Typography
                      variant="body1"
                      sx={{ fontWeight: 700 }}
                    >
                      Dispatch User
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {dispatchUser.email}
                    </Typography>
                  </Stack>
                </TableCell>
                <TableCell>
                  <Stack spacing={0.5}>
                    <Typography variant="body1" sx={{ fontWeight: 700 }}>
                      {dispatchUser.role === 'SUPER_ADMIN'
                        ? 'Super Admin'
                        : 'Dispatch'}
                    </Typography>
                    <Typography
                      variant="body2"
                      color={
                        dispatchUser.verified
                          ? 'text.primary'
                          : theme.palette.error.main
                      }
                    >
                      {dispatchUser.verified ? 'Active' : 'Invite Pending'}
                    </Typography>
                  </Stack>
                </TableCell>
                <TableCell align="right">
                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <Button
                      variant="contained"
                      size="small"
                      sx={{
                        textTransform: 'none',
                        fontWeight: 400,
                        fontSize: '15px',
                        borderRadius: '5px',
                        whiteSpace: 'nowrap',
                        px: 3.5,
                        minWidth: 140,
                      }}
                      onClick={() =>
                        handleOpenEditDialog(
                          dispatchUser.id,
                          dispatchUser.role === 'SUPER_ADMIN'
                            ? 'SUPER_ADMIN'
                            : 'DISPATCH'
                        )
                      }
                      disabled={isUpdating || isDeleting}
                    >
                      Edit Role
                    </Button>
                    <IconButton
                      onClick={() => handleDeleteDispatchUser(dispatchUser.id)}
                      disabled={isDeleting || isUpdating}
                      aria-label="Delete user"
                      sx={{
                        p: 0,
                      }}
                    >
                      <img
                        src={Delete}
                        alt="delete user"
                        width={28}
                        height={33}
                        style={{ cursor: 'pointer' }}
                      />
                    </IconButton>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </Box>
  )
}

export default SettingsUserManagement
